import React from 'react';

import PropTypes from 'prop-types';
import withAuth from '../../hocs/withAuth';
import Icon from '../../Img/Icon';
import { getPickleBallRating, getRatingDetails, getRatingProgressDisplay, setRatingChoice } from '../../../utils';
import { getOrganizerRating } from '../../../utils/events';
import PbrRating from '../PbrRating';
import { MatchFormatTypes } from '../../../utils/postScoreConstants';
import { sportTypeIds } from '../../../utils/eventConstants';
import { HISTORIC_RATING_MARKER, RatingDisplayStates } from '../../../utils/constants';

/** force:
 * - ratingType: singles/doubles
 * - utrType: sanctioned/myUtr
 * - verifiedIcon: true (show verified badge for verified rating)
 * - superscriptHistoricMarker: true (raise the trailing marker on a historic rating)
 * - hideHistoricMarker: true (strip the trailing marker rather than raising it into a superscript)
 * - historicVerifiedIcon: true (allow a historic rating to show the verified badge too, when the
 *   rating choice behind it was Verified* - otherwise a historic rating never shows one)
 * - verifiedTooltip: true (give the badge its own styled hover chip instead of the native `title`)
 */

// The badge's own hover text, distinct from the rating's status chip next to it - the checkmark says
// where the result came from, not how reliable the number is (TMS-2013 feedback row 32). Copy is
// verbatim from the Figma tooltip frame (node 1245-21087).
const VERIFIED_TOOLTIP = 'Based on Verified event results';

// The verified badge normally paints via the global `.verified16` background asset. A caller can
// opt into rendering it through the shared masked `Icon` instead — the Saved List design (TMS-2311)
// wants the Figma seal glyph in a solid `black` tone rather than the global blue asset.
const VERIFIED_ICON_VARIANT_SRC = {
    black: '/img/v3/icons/icon-check-seal.svg',
};
const VERIFIED_ICON_VARIANT_SIZE = 12;

const UtrNumber = (props) => {
    const {
        player,
        auth,
        pbrClass,
        isProgressionDraw,
        showLabel,
        organizerRatings,
        isOrganizerView,
        sportTypeId = sportTypeIds.TENNIS,
        force = {},
        isPickleballRating,
        isItGlobalSearch,
        'data-testid': dataTestId,
    } = props;
    const { ratingType, utrType, verifiedIcon, showDecimals, doNotMask, showProjectedViewStatus, superscriptHistoricMarker, hideHistoricMarker, historicVerifiedIcon, verifiedTooltip, verifiedIconVariant } = force;
    const userIsPowered = (auth.user && auth.user.isPowered) || false;
    const rating = getRatingDetails(setRatingChoice({ player, ratingType, utrType }), userIsPowered, showDecimals, doNotMask, showProjectedViewStatus);
    const showPBR = isProgressionDraw !== undefined ? isProgressionDraw && rating.showPBRRating : rating.showPBRRating;
    const isPickleBallSportType = sportTypeId === sportTypeIds.PICKLEBALL;

    const singlePkbRatings = player?.singlespkbrating || player?.historicpkbsinglesrating || player?.singlesPkbRatingDisplay?.pkbRating || player?.utrRange?.pkbRatingDisplay || player?.utrRange?.pickleballRatingDisplay;
    const doublesPkbRatings = player?.doublespkbrating || player?.historicpkbdoublesrating || player?.doublesPkbRatingDisplay?.pkbRating || player?.utrRange?.pkbRatingDisplay || player?.utrRange?.pickleballRatingDisplay;
    const singlePkbisVerified = player?.singlesPkbRatingDisplay?.isVerified;
    const doublesPkbisVerified = player?.doublesPkbRatingDisplay?.isVerified;

    const displayTennisRating = () => {
        if (rating.value === 'UR' && isItGlobalSearch) {
            return (
                <img
                    width={15}
                    src={'/img/v3/icons/icon-no-ratings.svg'}
                    data-testid={dataTestId && `${dataTestId}.unrated-icon`}
                />
            );
        }
        return rating.value;
    };
    const tennisDisplayRating = displayTennisRating();

    // `applyHistoricRating` bakes the marker into the value string (`utils/index.js`), so it can only
    // be raised by splitting the string here. The wrapping span keeps the value and its marker in one
    // inline box - consumers that centre `.value` with flex would otherwise make the `sup` a sibling
    // flex item, where `vertical-align: super` is ignored and the marker silently sits on the baseline.
    const renderTennisRatingValue = () => {
        const isHistoricValue = typeof tennisDisplayRating === 'string' && tennisDisplayRating.endsWith(HISTORIC_RATING_MARKER);
        if (hideHistoricMarker && isHistoricValue) {
            return (
                <span data-testid={dataTestId && `${dataTestId}.historic-value`}>
                    {tennisDisplayRating.slice(0, -HISTORIC_RATING_MARKER.length)}
                </span>
            );
        }
        if (superscriptHistoricMarker && isHistoricValue) {
            return (
                <span data-testid={dataTestId && `${dataTestId}.historic-value`}>
                    {tennisDisplayRating.slice(0, -HISTORIC_RATING_MARKER.length)}
                    <sup data-testid={dataTestId && `${dataTestId}.historic-marker`}>{HISTORIC_RATING_MARKER}</sup>
                </span>
            );
        }
        return tennisDisplayRating;
    };
    let organizerRating;
    let memberOrganizerRating;
    if (organizerRatings) {
        organizerRating = getOrganizerRating(player?.memberId, organizerRatings);
        memberOrganizerRating = ratingType === MatchFormatTypes.SINGLES ? organizerRating?.singlesOrganizerRating : organizerRating?.doublesOrganizerRating;
    }
    const getReliability = (playerRating) => {
        const reliabilityDisplay = getRatingProgressDisplay(playerRating.progress);
        return `(${reliabilityDisplay}%)`;
    };

    // Two independent guards normally block a historic rating from ever showing the verified badge:
    // `applyHistoricRating` (`utils/index.js`) always sets `rating.verified = false`, and a historic
    // rating's `rating.status` stays `Unrated` (that's how it reached the historic path at all). Both
    // have to give, and only for the historic state, and only when the caller opts in - `verified`
    // stays false for the 16 consumers that don't, so this reads `historicVerified` instead, which
    // `applyHistoricRating` publishes separately for exactly this purpose (TMS-2226).
    const isHistoricVerified = !!historicVerifiedIcon && rating.displayState === RatingDisplayStates.HISTORIC && !!rating.historicVerified;
    // Hoisted out of the JSX below, where it pushed the inline expression past the conditional-operator
    // limit. With `historicVerifiedIcon` absent `isHistoricVerified` is false and this collapses to the
    // original `value !== 'UR' && status !== 'Unrated' && verifiedIcon && verified` - unchanged for the
    // 16 consumers that don't opt in.
    const showVerifiedBadge = rating.value !== 'UR' &&
        verifiedIcon &&
        (isHistoricVerified || (rating.status !== 'Unrated' && rating.verified));

    // Without `verifiedTooltip` this is byte-for-byte the span the 16 other consumers already render,
    // native `title` and all. With it, the badge gains a shrink-wrapped anchor so a chip can be
    // positioned against the checkmark rather than the whole rating, and the native `title` is dropped
    // - keeping both would show two tooltips for one hover, the same trap `Rating`'s status chip hit.
    // The `.verified16` span stays nested inside, so rules and tests that reach for that class are
    // unaffected; the `.verified-icon` test id moves to the anchor so it still resolves to the badge.
    // `role="group"` + `aria-label` mirror the status chip: the chip is revealed by `:hover` from a
    // `visibility: hidden` base, so it never reaches assistive tech, and a bare `span` has the
    // implicit role `generic`, for which ARIA prohibits an author-supplied name.
    const variantSrc = VERIFIED_ICON_VARIANT_SRC[verifiedIconVariant];
    // The glyph the badge paints: the global `.verified16` asset by default, or the masked `Icon` when
    // a caller opts into a tone variant. Only the opted-in surface changes — every other consumer keeps
    // the `.verified16` span byte-for-byte.
    const renderBadgeGlyph = () => (
        variantSrc
            ? <Icon src={variantSrc} tone={verifiedIconVariant} size={VERIFIED_ICON_VARIANT_SIZE} />
            : <span className="verified16" />
    );

    const renderVerifiedBadge = () => {
        const badgeTestId = dataTestId && `${dataTestId}.verified-icon`;

        if (!verifiedTooltip) {
            if (variantSrc) {
                return (
                    <span className="verifiedAnchor" title="Verified" data-testid={badgeTestId}>
                        {renderBadgeGlyph()}
                    </span>
                );
            }
            return <span className="verified16" title="Verified" data-testid={badgeTestId} />;
        }

        return (
            <span className="verifiedAnchor" role="group" aria-label={VERIFIED_TOOLTIP} data-testid={badgeTestId}>
                {renderBadgeGlyph()}
                <span className="verifiedTooltip" aria-hidden="true" data-testid={dataTestId && `${dataTestId}.verified-tooltip`}>
                    {VERIFIED_TOOLTIP}
                </span>
            </span>
        );
    };

    if (isPickleballRating) {
        const isSingles = ratingType === MatchFormatTypes.SINGLES;
        const ratings = isSingles ? singlePkbRatings : doublesPkbRatings;
        const isVerified = isSingles ? singlePkbisVerified : doublesPkbisVerified;

        return (
            <div data-testid={dataTestId || undefined}>
                <span>{ratings || (isItGlobalSearch
                    ? (
                        <img
                            width={15}
                            src={'/img/v3/icons/icon-no-ratings.svg'}
                            data-testid={dataTestId && `${dataTestId}.unrated-icon`}
                        />
                    ) : 'UR'
                )}
                </span>
                {ratings && isVerified && <span className="verified15" title="Verified" data-testid={dataTestId && `${dataTestId}.verified-icon`} />}
            </div>
        );
    }

    if (isPickleBallSportType) {
        if (organizerRatings && memberOrganizerRating) {
            return (
                <>
                    <span
                        className="text-danger"
                    >
                        {memberOrganizerRating}
                    </span> ({getPickleBallRating(player, ratingType).value})
                </>
            );
        }

        return (
            <span data-testid={dataTestId || undefined}>
                {getPickleBallRating(player, ratingType).value}
            </span>
        );
    }

    if (isOrganizerView) {
        if (organizerRatings && memberOrganizerRating) {
            return (
                <>
                    <span className="text-danger">{memberOrganizerRating}</span>

                    ( {tennisDisplayRating}
                    {rating.status === ' Projected' ? ' P' : ''})
                </>
            );
        }
        // The organizer view keeps its own inline badge gate and renders `tennisDisplayRating`
        // straight, so `hideHistoricMarker` and `historicVerifiedIcon` are both inert here - only
        // the main return below routes through `renderTennisRatingValue()` / `showVerifiedBadge`.
        // No caller passes `showProjectedViewStatus` alongside either of them today; one that did
        // would silently get neither rather than an error.
        return (
            (showPBR) ? (
                <PbrRating pbrClass={pbrClass} showLabel={showLabel} pbrRating={rating.pbrRating} data-testid={dataTestId && `${dataTestId}.pbr-rating`} />
            ) : (
                <span data-testid={dataTestId || undefined}> {tennisDisplayRating} {rating.value !== 'UR' && rating.status !== 'Unrated' && verifiedIcon && rating.verified ? renderVerifiedBadge() : ''}{rating.status === 'Projected' ? getReliability(rating) : ''}
                </span>
            )
        );
    }

    return (
        (showPBR) ? (
            <PbrRating pbrClass={pbrClass} showLabel={showLabel} pbrRating={rating.pbrRating} />
        ) : (
            <>
                {renderTennisRatingValue()} {showVerifiedBadge ? renderVerifiedBadge() : ''}
            </>
        )
    );
};

UtrNumber.propTypes = {
    isPickleballRating: PropTypes.bool,
    player: PropTypes.instanceOf(Object).isRequired,
    isItGlobalSearch: PropTypes.bool,
    'data-testid': PropTypes.string,
};

UtrNumber.defaultProps = {
    isPickleballRating: false,
    isItGlobalSearch: false,
    'data-testid': null,
};
export default withAuth(UtrNumber);
